import { useEffect, useRef, useState } from "react"
import { useDevices } from "@daily-co/daily-react"
import { Camera, Mic, Settings, Sparkles, Volume2 } from "lucide-react"
import { readBackgroundEffect, SOFT_BLUR_STRENGTH, supportsBackgroundEffects } from "./BackgroundEffects"
import { CallSelect } from "./CallSelect"
import { deviceOptions } from "./DeviceSettingsMenu"
import type { CallSettingsPane } from "./CallSettingsDialog"
import { changeVideoEffect, useVideoEffects } from "./videoEffects"

type QuickDeviceMenuProps = {
  kind: "microphone" | "camera"
  onOpenSettings: (pane: CallSettingsPane) => void
  onClose: () => void
}

function QuickSelect({ id, label, icon, devices, currentId, onChange, emptyLabel, disabled }: { id: string; label: string; icon: React.ReactNode; devices: ReturnType<typeof useDevices>["cameras"]; currentId?: string; onChange: (deviceId: string) => void; emptyLabel: string; disabled?: boolean }) {
  return <CallSelect id={id} label={label} icon={icon} value={currentId || ""} options={deviceOptions(devices, label)} onChange={onChange} placeholder={emptyLabel} openUp disabled={disabled} />
}

/**
 * The small menu behind the chevron beside the microphone or camera button, as in Google Meet:
 * the device choice (microphone and speaker, or camera with a blur toggle and an effects
 * shortcut) and a settings cog that opens the matching pane of the Settings dialog.
 */
export function QuickDeviceMenu({ kind, onOpenSettings, onClose }: QuickDeviceMenuProps) {
  const { cameras, microphones, speakers, currentCam, currentMic, currentSpeaker, setCamera, setMicrophone, setSpeaker } = useDevices()
  const effects = useVideoEffects()
  const changingBlur = effects.pending !== null
  const blurError = effects.error
  const [changingDevice, setChangingDevice] = useState(false)
  const [deviceError, setDeviceError] = useState("")
  const changeDevice = async (label: string, change: () => Promise<void>) => {
    if (changingDevice) return
    setChangingDevice(true)
    setDeviceError("")
    try { await change() } catch { setDeviceError(`Could not change the ${label}. Check the device and try again.`) } finally { setChangingDevice(false) }
  }
  const rootRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    rootRef.current?.querySelector<HTMLElement>("[role=combobox]")?.focus()
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) onClose()
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [onClose])

  const blurOn = readBackgroundEffect(effects.desired ? { video: { processor: effects.desired } } : effects.inputSettings) !== "none"
  const toggleBlur = async () => {
    if (!effects.call || changingBlur) return
    await changeVideoEffect(effects.call, blurOn ? { type: "none" } : { type: "background-blur", config: { strength: SOFT_BLUR_STRENGTH } })
  }
  const effectsSupported = supportsBackgroundEffects()
  const settingsPane: CallSettingsPane = kind === "microphone" ? "audio" : "video"

  return (
    <div
      ref={rootRef}
      role="group"
      aria-label={kind === "microphone" ? "Microphone options" : "Camera options"}
      data-testid={kind === "microphone" ? "call-mic-menu" : "call-cam-menu"}
      className="absolute bottom-24 left-1/2 z-[45] w-[min(32rem,calc(100vw-1.5rem))] max-w-[calc(100%-1.5rem)] -translate-x-1/2 space-y-3 rounded-2xl bg-[#0f1419] p-3 text-sm text-white shadow-2xl ring-1 ring-white/10"
    >
      {kind === "microphone" ? (
        <>
          <div className="min-w-0">
            <QuickSelect id="call-quick-microphone" label="Microphone" icon={<Mic className="h-4 w-4" />} devices={microphones} currentId={currentMic?.device.deviceId} onChange={(id) => void changeDevice("microphone", () => setMicrophone(id))} emptyLabel="No microphone found" disabled={changingDevice} />
          </div>
          {speakers.length > 0 && (
            <div className="min-w-0">
              <QuickSelect id="call-quick-speaker" label="Speaker" icon={<Volume2 className="h-4 w-4" />} devices={speakers} currentId={currentSpeaker?.device.deviceId} onChange={(id) => void changeDevice("speaker", () => setSpeaker(id))} emptyLabel="Default speaker" disabled={changingDevice} />
            </div>
          )}
        </>
      ) : (
        <div className="min-w-0">
          <QuickSelect id="call-quick-camera" label="Camera" icon={<Camera className="h-4 w-4" />} devices={cameras} currentId={currentCam?.device.deviceId} onChange={(id) => void changeDevice("camera", () => setCamera(id))} emptyLabel="No camera found" disabled={changingDevice} />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {kind === "camera" && effectsSupported && (
          <>
            <button
              type="button"
              aria-pressed={blurOn}
              data-testid="call-quick-blur"
              onClick={() => void toggleBlur()}
              disabled={changingBlur}
              aria-describedby={blurError ? "call-quick-blur-error" : undefined}
              className={`min-h-10 rounded-lg px-3 py-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c] ${blurOn ? "bg-white text-[#1c2530]" : "bg-white/10 hover:bg-white/20"}`}
            >
              Blur background
            </button>
            <button
              type="button"
              data-testid="call-quick-effects"
              onClick={() => {
                onOpenSettings("effects")
                onClose()
              }}
              className="flex min-h-10 items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c]"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Backgrounds and effects
            </button>
          </>
        )}
        <button
          type="button"
          aria-label="Settings"
          title="Settings"
          data-testid={kind === "microphone" ? "call-quick-audio-settings" : "call-quick-video-settings"}
          onClick={() => {
            onOpenSettings(settingsPane)
            onClose()
          }}
          className="ml-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white/80 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c]"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>
      {(deviceError || (kind === "camera" && blurError)) && <p id="call-quick-blur-error" role="alert" className="text-xs text-amber-200">{deviceError || blurError}</p>}
    </div>
  )
}
